import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Plus, Search, FileText, X, Eye, Trash2, PenTool, Upload, Play, Sparkles, Download, AlertCircle, Files, Edit, Building2, ExternalLink, MessageSquare } from 'lucide-react';
import { agreementService, AgreementDocument, uploadAgreementDocument } from '@/firebase/agreementService';
import { agreementCompletionService, AgreementCompletion } from '@/firebase/agreementCompletionService';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useNotification } from '@/contexts/NotificationContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { usePreview } from '@/contexts/PreviewContext';
import { 
  fetchUserRoles, 
  normalizeRoles,
  isSystemManager as checkIsSystemManager,
  getUserSystemRoleForOrganization,
  getUserOwnerRolesForOrganization,
  isSystemUserInOrganization,
  isManagerInOrganization
} from '@/utils/roleHelpers';
import { SYSTEM_ROLES } from '@/constants/roles';
import { locationService, Location } from '@/firebase/locationService';
import { complianceService, ComplianceTemplate } from '@/firebase/complianceService';
import { aiSearchService, NewAgreementSuggestion } from '@/services/aiSearchService';
import { vendorService, Vendor } from '@/firebase/vendorService';
import { employeeService, Employee } from '@/firebase/employeeService';
import { assetService, AssetType } from '@/firebase/assetService';
import { useNavigate } from 'react-router-dom';
import RunAgreement from './RunAgreement';
import VendorForm from './VendorForm';
import EmployeeForm from './EmployeeForm';
import BulkUploadVendorContracts from './BulkUploadVendorContracts';
import { messagingIntegrationService } from '@/services/messagingIntegrationService';
import ContextualHelp from './ContextualHelp';

const AgreementManagement: React.FC = () => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { previewState, isPreviewMode } = usePreview();
  const { showNotification } = useNotification();
  const { hasFeature, subscription } = useSubscription();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<AgreementDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocument, setSelectedDocument] = useState<AgreementDocument | null>(null);
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyDocument, setHistoryDocument] = useState<AgreementDocument | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<{ id: string; name: string } | null>(null);
  const [availableLocations, setAvailableLocations] = useState<Location[]>([]);
  const [templates, setTemplates] = useState<ComplianceTemplate[]>([]);
  const [templateMap, setTemplateMap] = useState<Record<string, string>>({});
  const [newAgreementSuggestions, setNewAgreementSuggestions] = useState<NewAgreementSuggestion[] | null>(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [agreementToAdd, setAgreementToAdd] = useState<Partial<AgreementDocument> | null>(null);
  const [agreementDocumentContent, setAgreementDocumentContent] = useState<string | null>(null);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [userOwnerRoles, setUserOwnerRoles] = useState<string[]>([]);
  const [isSystemUser, setIsSystemUser] = useState<boolean>(false);
  const [isSystemManager, setIsSystemManager] = useState<boolean>(false);
  const [templateMapWithRoles, setTemplateMapWithRoles] = useState<Record<string, ComplianceTemplate>>({});
  
  // Vendor management state
  const [activeTab, setActiveTab] = useState<'agreements' | 'vendors' | 'employees'>('agreements');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendorConversations, setVendorConversations] = useState<Map<string, string>>(new Map()); // vendorId -> conversationId
  const [isLoadingVendors, setIsLoadingVendors] = useState(false);
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [isEditingVendor, setIsEditingVendor] = useState(false);
  const [showDeleteVendorConfirm, setShowDeleteVendorConfirm] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState<{ id: string; name: string } | null>(null);
  const [showVendorDetailModal, setShowVendorDetailModal] = useState(false);
  const [selectedVendorForDetail, setSelectedVendorForDetail] = useState<Vendor | null>(null);
  const [vendorContracts, setVendorContracts] = useState<AgreementCompletion[]>([]);
  const [isLoadingVendorContracts, setIsLoadingVendorContracts] = useState(false);
  const [vendorDetailTab, setVendorDetailTab] = useState<'contracts' | 'documents'>('contracts');
  const [showAISummaryModal, setShowAISummaryModal] = useState(false);
  const [selectedContractSummary, setSelectedContractSummary] = useState<string>('');
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  
  // Employee management state
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [isEditingEmployee, setIsEditingEmployee] = useState(false);
  const [showDeleteEmployeeConfirm, setShowDeleteEmployeeConfirm] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    loadDocuments();
    loadLocations();
    loadTemplates();
    loadUserRoles();
    loadVendors();
    loadEmployees();
  }, [currentOrganization?.id, user?.id, isPreviewMode, previewState?.previewType, previewState?.previewUserId, previewState?.role]);

  const loadVendorConversations = async (vendorIds: string[]) => {
    if (!currentOrganization?.id || vendorIds.length === 0) return;
    
    try {
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const { db } = await import('@/firebase/config');
      
      const conversationsMap = new Map<string, string>();
      
      // Query conversations linked to any of these vendors
      const conversationsRef = collection(db, 'conversations');
      
      // Firestore 'in' queries are limited to 10 items, so we need to batch
      const batchSize = 10;
      for (let i = 0; i < vendorIds.length; i += batchSize) {
        const batch = vendorIds.slice(i, i + batchSize);
        
        try {
          const conversationsQuery = query(
            conversationsRef,
            where('linkedItems.vendorId', 'in', batch)
          );
          const conversationsSnapshot = await getDocs(conversationsQuery);
          
          conversationsSnapshot.forEach((doc) => {
            const data = doc.data();
            const vendorId = data.linkedItems?.vendorId;
            if (vendorId && batch.includes(vendorId)) {
              conversationsMap.set(vendorId, doc.id);
            }
          });
        } catch (error: any) {
          // If index error, try individual queries as fallback
          if (error.code === 'failed-precondition' || error.message?.includes('index')) {
            console.warn('Batch query failed, trying individual queries:', error);
            for (const vendorId of batch) {
              try {
                const vendorQuery = query(
                  conversationsRef,
                  where('linkedItems.vendorId', '==', vendorId)
                );
                const vendorSnapshot = await getDocs(vendorQuery);
                vendorSnapshot.forEach((doc) => {
                  const data = doc.data();
                  if (data.linkedItems?.vendorId === vendorId) {
                    conversationsMap.set(vendorId, doc.id);
                  }
                });
              } catch (err) {
                console.error(`Error loading conversation for vendor ${vendorId}:`, err);
              }
            }
          } else {
            console.error('Error loading vendor conversations:', error);
          }
        }
      }
      
      setVendorConversations(conversationsMap);
    } catch (error) {
      console.error('Error loading vendor conversations:', error);
    }
  };

  // Load vendors
  const loadVendors = async () => {
    if (!currentOrganization?.id) return;
    
    setIsLoadingVendors(true);
    try {
      const result = await vendorService.getAllVendors(currentOrganization.id);
      if (result.success && result.data) {
        setVendors(result.data);
        
        // Load conversations for all vendors
        const vendorIds = result.data.map(v => v.id).filter(Boolean) as string[];
        if (vendorIds.length > 0) {
          await loadVendorConversations(vendorIds);
        }
      } else {
        showNotification(`Error loading vendors: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error loading vendors:', error);
      showNotification('Failed to load vendors', 'error');
    } finally {
      setIsLoadingVendors(false);
    }
  };

  // Load employees
  const loadEmployees = async () => {
    if (!currentOrganization?.id) return;
    
    setIsLoadingEmployees(true);
    try {
      const result = await employeeService.getAllEmployees(currentOrganization.id);
      if (result.success && result.data) {
        setEmployees(result.data);
      } else {
        showNotification(`Error loading employees: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error loading employees:', error);
      showNotification('Failed to load employees', 'error');
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  const loadUserRoles = async () => {
    // In preview mode, use preview user's roles
    if (isPreviewMode && previewState?.previewType === 'user' && previewState.previewUserId) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const previewUserRef = doc(db, 'users', previewState.previewUserId);
        const previewUserSnap = await getDoc(previewUserRef);
        
        if (previewUserSnap.exists()) {
          const previewUserData = previewUserSnap.data();
          const organizationId = currentOrganization?.id;
          
          // Use organization-specific roles if organization is available
          if (organizationId) {
            const ownerRoles = getUserOwnerRolesForOrganization(previewUserData, organizationId);
            setUserOwnerRoles(ownerRoles);
            setIsSystemUser(isSystemUserInOrganization(previewUserData, organizationId));
            setIsSystemManager(isManagerInOrganization(previewUserData, organizationId));
          } else {
            // Fallback to global roles if no organization
            const previewUserRoles = normalizeRoles(previewUserData?.roles || previewUserData?.role);
            const ownerRoles = previewUserRoles.filter((r: string) => !SYSTEM_ROLES.includes(r.toLowerCase() as any));
            setUserOwnerRoles(ownerRoles);
            const systemRoles = previewUserRoles.filter((r: string) => SYSTEM_ROLES.includes(r.toLowerCase() as any));
            const isSystemUserRole = systemRoles.length === 1 && systemRoles[0].toLowerCase() === 'user';
            setIsSystemUser(isSystemUserRole);
            const isManagerRole = previewUserRoles.some((r: string) => ['manager', 'admin'].includes(r.toLowerCase()));
            setIsSystemManager(isManagerRole);
          }
        }
      } catch (error) {
        console.error('Error loading preview user roles:', error);
      }
    } else if (isPreviewMode && previewState?.previewType === 'role') {
      // Preview by role: determine permissions based on role
      const role = previewState.role.toLowerCase();
      const isSystemUserRole = role === 'user';
      const isManagerRole = role === 'manager' || role === 'admin';
      
      setIsSystemUser(isSystemUserRole);
      setIsSystemManager(isManagerRole);
      setUserOwnerRoles([]); // No owner roles when previewing by role
    } else if (user?.id && currentOrganization?.id) {
      // Normal mode: use actual user's roles for current organization
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const organizationId = currentOrganization.id;
          
          // Get organization-specific roles
          const ownerRoles = getUserOwnerRolesForOrganization(userData, organizationId);
          const isSystemUserRole = isSystemUserInOrganization(userData, organizationId);
          const isManagerRole = isManagerInOrganization(userData, organizationId);
          
          setUserOwnerRoles(ownerRoles);
          setIsSystemUser(isSystemUserRole);
          setIsSystemManager(isManagerRole);
        } else {
          // Fallback to legacy fetchUserRoles
          const roles = await fetchUserRoles(user.id);
          setUserOwnerRoles(roles.ownerRoles);
          setIsSystemUser(roles.isSystemUser);
          setIsSystemManager(roles.isSystemManager);
        }
      } catch (error) {
        console.error('Error loading user roles:', error);
        // Fallback to legacy fetchUserRoles
        const roles = await fetchUserRoles(user.id);
        setUserOwnerRoles(roles.ownerRoles);
        setIsSystemUser(roles.isSystemUser);
        setIsSystemManager(roles.isSystemManager);
      }
    } else if (user?.id && !currentOrganization?.id) {
      // No organization - use legacy roles
      const roles = await fetchUserRoles(user.id);
      setUserOwnerRoles(roles.ownerRoles);
      setIsSystemUser(roles.isSystemUser);
      setIsSystemManager(roles.isSystemManager);
    }
  };

  useEffect(() => {
    // Create a map of templateId -> templateName
    const map: Record<string, string> = {};
    templates.forEach(t => {
      if (t.id) {
        map[t.id] = t.name;
      }
    });
    setTemplateMap(map);

    // Create a map of templateId -> full ComplianceTemplate (with owner_role)
    const templatesWithRolesMap: Record<string, ComplianceTemplate> = {};
    templates.forEach(t => {
      if (t.id) {
        templatesWithRolesMap[t.id] = t;
      }
    });
    setTemplateMapWithRoles(templatesWithRolesMap);
  }, [templates]);

  // Debounced AI search when no results are found
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Clear AI suggestion when search term is cleared
    if (!searchTerm) {
      setNewAgreementSuggestions(null);
      return;
    }

    // Calculate filtered agreements inline to check if we have results
    const currentFiltered = documents.filter(doc =>
      doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Only trigger AI search if there are no filtered agreements and search term is long enough
    const hasResults = currentFiltered.length > 0;
    
    if (!hasResults && searchTerm.length >= 3) {
      // Debounce AI search by 800ms
      searchTimeoutRef.current = setTimeout(async () => {
        setIsLoadingAI(true);
        setNewAgreementSuggestions(null);
        
        try {
          // Get available templates
          const availableTemplates = templates;
          
          // Check if user has any templates first
          if (availableTemplates.length === 0) {
            // No templates available - can't create agreements without templates
            setIsLoadingAI(false);
            setNewAgreementSuggestions([] as any);
            return;
          }
          
          const existingAgreementNames = documents.map(doc => doc.name);
          
          // Try AI generation for agreements
          const newSuggestions = await aiSearchService.generateNewAgreementSuggestions(
            searchTerm, 
            existingAgreementNames,
            availableTemplates
          );
          
          if (newSuggestions && newSuggestions.length > 0) {
            setNewAgreementSuggestions(newSuggestions);
          } else {
            // No suggestions from AI
            setNewAgreementSuggestions([] as any);
          }
        } catch (error) {
          console.error('Error fetching AI agreement suggestions:', error);
        } finally {
          setIsLoadingAI(false);
        }
      }, 800);
    } else {
      setNewAgreementSuggestions(null);
      setIsLoadingAI(false);
    }

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, documents, templates]);

  const loadTemplates = async () => {
    const result = await complianceService.getAllTemplates(currentOrganization?.id);
    if (result.success && result.data) {
      setTemplates(result.data);
    }
  };

  const loadLocations = async () => {
    let userLocationIds: string[] | undefined;
    let userRole: string | undefined;
    
    if (user?.id) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          userLocationIds = userData.locationIds;
          // Use organization-specific role if available
          if (currentOrganization?.id) {
            userRole = getUserSystemRoleForOrganization(userData, currentOrganization.id) || undefined;
          } else {
            // Fallback to legacy role if no organization
            userRole = userData.role;
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }

    const result = await locationService.getAccessibleLocations(
      currentOrganization?.id,
      user?.id,
      userRole,
      userLocationIds
    );
    
    if (result.success && result.data) {
      setAvailableLocations(result.data.filter(loc => loc.id && loc.name));
    }
  };

  const loadDocuments = async () => {
    if (!currentOrganization?.id) {
      setDocuments([]);
      return;
    }

    setIsLoading(true);
    try {
      const result = await agreementService.getAllDocuments(currentOrganization.id);
      if (result.success && result.data) {
        setDocuments(result.data);
      }
    } catch (error) {
      console.error('Error loading agreement documents:', error);
      showNotification('Failed to load documents', 'error');
    } finally {
      setIsLoading(false);
    }
  };


  const handleRunAgreement = (document: AgreementDocument) => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to add signed contracts.', 'error');
      navigate('/subscription');
      return;
    }

    setSelectedDocument(document);
    setIsRunning(true);
    setShowHistory(false);
    setHistoryDocument(null);
  };

  const handleViewHistory = (document: AgreementDocument) => {
    setHistoryDocument(document);
    setShowHistory(true);
    setIsRunning(false);
    setSelectedDocument(null);
  };

  const handleOpenVendorConversation = async (vendor: Vendor) => {
    if (!currentOrganization?.id || !user?.id || !vendor.id) {
      showNotification('Unable to open vendor conversation. Please ensure you are logged in.', 'error');
      return;
    }

    try {
      const result = await messagingIntegrationService.getOrCreateVendorConversation(
        vendor.id,
        vendor.name,
        currentOrganization.id,
        user.id
      );

      if (result.success && result.data?.id) {
        // Open messages page in a new window/tab with conversation ID
        window.open(`/messages?conversation=${result.data.id}`, '_blank');
      } else {
        showNotification(result.error || 'Failed to open vendor conversation', 'error');
      }
    } catch (error: any) {
      console.error('Error opening vendor conversation:', error);
      showNotification(error.message || 'Failed to open vendor conversation', 'error');
    }
  };

  const handleOpenEmployeeConversation = async (employee: Employee) => {
    if (!currentOrganization?.id || !user?.id || !employee.id) {
      showNotification('Unable to open employee conversation. Please ensure you are logged in.', 'error');
      return;
    }

    try {
      const result = await messagingIntegrationService.getOrCreateEmployeeConversation(
        employee.id,
        employee.name,
        currentOrganization.id,
        user.id
      );

      if (result.success && result.data?.id) {
        // Open messages page in a new window/tab with conversation ID
        window.open(`/messages?conversation=${result.data.id}`, '_blank');
      } else {
        showNotification(result.error || 'Failed to open employee conversation', 'error');
      }
    } catch (error: any) {
      console.error('Error opening employee conversation:', error);
      showNotification(error.message || 'Failed to open employee conversation', 'error');
    }
  };

  const handleCloseRunAgreement = () => {
    setIsRunning(false);
    setSelectedDocument(null);
    setShowHistory(false);
    setHistoryDocument(null);
    loadDocuments();
  };

  const handleDeleteClick = (documentId: string) => {
    // Check if user has permission to delete (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can delete contract types', 'error');
      return;
    }
    // Check if user has permission to delete (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can delete contract types', 'error');
      return;
    }
    // Find the document to get its name for the confirmation message
    const doc = documents.find(d => d.id === documentId);
    if (doc) {
      setDocumentToDelete({ id: documentId, name: doc.name });
      setShowDeleteConfirm(true);
    }
  };

  // Vendor handlers
  const handleAddVendor = () => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to add vendors.', 'error');
      navigate('/subscription');
      return;
    }

    setSelectedVendor(null);
    setIsEditingVendor(false);
    setShowVendorForm(true);
  };

  const handleBulkUploadClick = () => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to bulk upload contracts.', 'error');
      navigate('/subscription');
      return;
    }

    setShowBulkUploadModal(true);
  };

  const handleEditVendor = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setIsEditingVendor(true);
    setShowVendorForm(true);
  };

  const handleSaveVendor = async (vendorData: Partial<Vendor>) => {
    if (!currentOrganization?.id) {
      showNotification('Organization ID is required', 'error');
      return;
    }

    try {
      if (isEditingVendor && selectedVendor?.id) {
        // Update existing vendor
        const result = await vendorService.updateVendor(selectedVendor.id, vendorData);
        if (result.success) {
          showNotification('Vendor updated successfully', 'success');
          await loadVendors();
          setShowVendorForm(false);
          setSelectedVendor(null);
          setIsEditingVendor(false);
        } else {
          showNotification(`Error: ${result.error}`, 'error');
        }
      } else {
        // Check if subscription is cancelled before adding new vendor
        if (subscription?.status === 'canceled') {
          showNotification('Your subscription has been cancelled. Please renew your subscription to add vendors.', 'error');
          navigate('/subscription');
          setShowVendorForm(false);
          return;
        }

        // Add new vendor - validate required fields
        if (!vendorData.name || !vendorData.name.trim()) {
          showNotification('Vendor name is required', 'error');
          return;
        }
        const result = await vendorService.addVendor(vendorData as Omit<Vendor, 'id'>, currentOrganization.id);
        if (result.success) {
          showNotification('Vendor added successfully', 'success');
          await loadVendors();
          setShowVendorForm(false);
          setSelectedVendor(null);
        } else {
          showNotification(`Error: ${result.error}`, 'error');
        }
      }
    } catch (error: any) {
      console.error('Error saving vendor:', error);
      showNotification('Failed to save vendor', 'error');
    }
  };

  const handleCancelVendor = () => {
    setShowVendorForm(false);
    setSelectedVendor(null);
    setIsEditingVendor(false);
  };

  const handleDeleteVendorClick = (vendorId: string) => {
    const vendor = vendors.find(v => v.id === vendorId);
    if (vendor) {
      setVendorToDelete({ id: vendorId, name: vendor.name });
      setShowDeleteVendorConfirm(true);
    }
  };

  const loadAssetTypes = async () => {
    if (!currentOrganization?.id) return;
    
    try {
      const result = await assetService.getAllAssetTypes(currentOrganization.id);
      if (result.success && result.data) {
        setAssetTypes(result.data);
      }
    } catch (error) {
      console.error('Error loading asset types:', error);
    }
  };

  const handleVendorClick = async (vendor: Vendor) => {
    setShowVendorDetailModal(true);
    setIsLoadingVendorContracts(true);
    setVendorDetailTab('contracts'); // Reset to contracts tab when opening
    
    // Load asset types when opening vendor detail modal
    await loadAssetTypes();
    
    try {
      if (vendor.id && currentOrganization?.id) {
        // Reload vendor data to get latest supplementary documents
        const vendorResult = await vendorService.getVendor(vendor.id);
        if (vendorResult.success && vendorResult.data) {
          setSelectedVendorForDetail(vendorResult.data);
        } else {
          setSelectedVendorForDetail(vendor);
        }
        
        console.log(`[handleVendorClick] Loading contracts for vendor: ${vendor.name} (ID: ${vendor.id})`);
        const result = await agreementCompletionService.getCompletionsForVendor(
          vendor.id, 
          currentOrganization.id,
          vendor.name // Pass vendor name as fallback
        );
        console.log(`[handleVendorClick] Result:`, result);
        if (result.success && result.data) {
          console.log(`[handleVendorClick] Found ${result.data.length} contracts`);
          setVendorContracts(result.data);
        } else {
          console.log(`[handleVendorClick] No contracts found or error:`, result.error);
          setVendorContracts([]);
        }
      } else {
        console.log(`[handleVendorClick] Missing vendor.id or organizationId`);
        setSelectedVendorForDetail(vendor);
        setVendorContracts([]);
      }
    } catch (error) {
      console.error('Error loading vendor contracts:', error);
      setSelectedVendorForDetail(vendor);
      setVendorContracts([]);
    } finally {
      setIsLoadingVendorContracts(false);
    }
  };

  const handleUploadSupplementaryDocument = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to upload contracts.', 'error');
      navigate('/subscription');
      e.target.value = ''; // Reset file input
      return;
    }

    const files = e.target.files;
    if (!files || files.length === 0 || !selectedVendorForDetail?.id) return;

    try {
      for (const file of Array.from(files)) {
        // Classify document type
        const { classifyDocument } = await import('@/services/geminiService');
        let documentType: 'communication' | 'amendment' | 'certificate' | 'license' | 'other' = 'other';
        
        try {
          const classification = await classifyDocument(file);
          documentType = classification.documentType === 'communication' ? 'communication' :
                        classification.documentType === 'amendment' ? 'amendment' :
                        classification.documentType === 'certificate' ? 'certificate' :
                        classification.documentType === 'license' ? 'license' : 'other';
        } catch (error) {
          console.error('Error classifying document:', error);
          // Use filename heuristics as fallback
          const fileName = file.name.toLowerCase();
          if (fileName.includes('email') || fileName.includes('letter') || fileName.includes('communication')) {
            documentType = 'communication';
          } else if (fileName.includes('amendment') || fileName.includes('addendum')) {
            documentType = 'amendment';
          } else if (fileName.includes('certificate') || fileName.includes('cert')) {
            documentType = 'certificate';
          } else if (fileName.includes('license') || fileName.includes('lic')) {
            documentType = 'license';
          }
        }

        // Upload document
        const result = await vendorService.uploadSupplementaryDocument(
          selectedVendorForDetail.id,
          file,
          documentType,
          user ? { id: user.id, email: user.email, name: user.name } : undefined
        );

        if (!result.success) {
          showNotification(`Failed to upload ${file.name}: ${result.error}`, 'error');
        }
      }

      // Reload vendor data to show new documents
      if (selectedVendorForDetail.id) {
        const vendorResult = await vendorService.getVendor(selectedVendorForDetail.id);
        if (vendorResult.success && vendorResult.data) {
          setSelectedVendorForDetail(vendorResult.data);
        }
      }

      showNotification(`Successfully uploaded ${files.length} document(s)`, 'success');
      
      // Reset file input
      e.target.value = '';
    } catch (error: any) {
      console.error('Error uploading supplementary document:', error);
      showNotification(`Error uploading document: ${error.message}`, 'error');
    }
  };

  const handleDeleteVendorConfirm = async () => {
    if (!vendorToDelete) return;

    try {
      const result = await vendorService.deleteVendor(vendorToDelete.id);
      if (result.success) {
        showNotification(`Vendor "${vendorToDelete.name}" deleted successfully`, 'success');
        await loadVendors();
        setShowDeleteVendorConfirm(false);
        setVendorToDelete(null);
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error deleting vendor:', error);
      showNotification('Failed to delete vendor', 'error');
    }
  };

  // Employee handlers
  const handleAddEmployee = () => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to add employees.', 'error');
      navigate('/subscription');
      return;
    }

    setSelectedEmployee(null);
    setIsEditingEmployee(false);
    setShowEmployeeForm(true);
  };

  const handleEditEmployee = (employee: Employee) => {
    setSelectedEmployee(employee);
    setIsEditingEmployee(true);
    setShowEmployeeForm(true);
  };

  const handleSaveEmployee = async (employeeData: Partial<Employee>) => {
    if (!currentOrganization?.id) {
      showNotification('Organization ID is required', 'error');
      return;
    }

    try {
      if (isEditingEmployee && selectedEmployee?.id) {
        // Update existing employee
        const result = await employeeService.updateEmployee(selectedEmployee.id, employeeData);
        if (result.success) {
          showNotification('Employee updated successfully', 'success');
          await loadEmployees();
          setShowEmployeeForm(false);
          setSelectedEmployee(null);
          setIsEditingEmployee(false);
        } else {
          showNotification(`Error: ${result.error}`, 'error');
        }
      } else {
        // Check if subscription is cancelled before adding new employee
        if (subscription?.status === 'canceled') {
          showNotification('Your subscription has been cancelled. Please renew your subscription to add employees.', 'error');
          navigate('/subscription');
          setShowEmployeeForm(false);
          return;
        }

        // Add new employee - validate required fields
        if (!employeeData.name || !employeeData.name.trim()) {
          showNotification('Employee name is required', 'error');
          return;
        }
        const result = await employeeService.addEmployee(employeeData as Omit<Employee, 'id'>, currentOrganization.id);
        if (result.success) {
          showNotification('Employee added successfully', 'success');
          await loadEmployees();
          setShowEmployeeForm(false);
          setSelectedEmployee(null);
        } else {
          showNotification(`Error: ${result.error}`, 'error');
        }
      }
    } catch (error: any) {
      console.error('Error saving employee:', error);
      showNotification('Failed to save employee', 'error');
    }
  };

  const handleCancelEmployee = () => {
    setShowEmployeeForm(false);
    setSelectedEmployee(null);
    setIsEditingEmployee(false);
  };

  const handleDeleteEmployeeClick = (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      setEmployeeToDelete({ id: employeeId, name: employee.name });
      setShowDeleteEmployeeConfirm(true);
    }
  };

  const handleDeleteEmployeeConfirm = async () => {
    if (!employeeToDelete) return;

    try {
      const result = await employeeService.deleteEmployee(employeeToDelete.id);
      if (result.success) {
        showNotification(`Employee "${employeeToDelete.name}" deleted successfully`, 'success');
        await loadEmployees();
        setShowDeleteEmployeeConfirm(false);
        setEmployeeToDelete(null);
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error deleting employee:', error);
      showNotification('Failed to delete employee', 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    try {
      const result = await agreementService.deleteDocument(documentToDelete.id);
      if (result.success) {
        showNotification(`Contract "${documentToDelete.name}" deleted successfully`, 'success');
        loadDocuments();
        setShowDeleteConfirm(false);
        setDocumentToDelete(null);
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      showNotification('Failed to delete contract', 'error');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDocumentToDelete(null);
  };

  const handleAddClick = () => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to add contract templates.', 'error');
      navigate('/subscription');
      return;
    }

    // Check if user has permission to add contract types (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can add new contract types', 'error');
      return;
    }

    if (!currentOrganization?.id) {
      setShowOrgModal(true);
      return;
    }
    setShowDocumentForm(true);
    setIsEditing(false);
    setSelectedDocument(null);
    setAgreementToAdd(null);
  };

  const handleEdit = (document: AgreementDocument) => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to edit contract templates.', 'error');
      navigate('/subscription');
      return;
    }

    // Check if user has permission to edit (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can edit contract types', 'error');
      return;
    }
    setSelectedDocument(document);
    setIsEditing(true);
    setShowDocumentForm(true);
  };

  const handleView = (document: AgreementDocument) => {
    setSelectedDocument(document);
    setShowViewModal(true);
  };

  const handleAddFromSuggestion = (suggestion: NewAgreementSuggestion) => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to add contract templates.', 'error');
      navigate('/subscription');
      return;
    }

    // Check if user has permission to add contract types (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can add new contract types', 'error');
      return;
    }

    if (!currentOrganization?.id) {
      setShowOrgModal(true);
      return;
    }

    // Convert AI suggestion to agreement format
    const agreementData: Partial<AgreementDocument> = {
      name: suggestion.name,
      description: suggestion.description,
      templateId: suggestion.templateId,
      status: 'draft',
      signers: suggestion.suggestedSigners.map(signer => ({
        name: signer.name,
        email: signer.email || '',
        role: signer.role || 'Signer',
        status: 'pending' as const
      }))
    };
    
    setAgreementToAdd(agreementData);
    setAgreementDocumentContent(suggestion.documentContent || null);
    setShowDocumentForm(true);
    setIsEditing(false);
    setSelectedDocument(null);
    // Clear search to see the new agreement after creation
    setSearchTerm('');
  };

  const filteredDocuments = documents.filter(doc => {
    // Apply search filter
    const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // For users with system role "User", filter by owner roles from compliance template
    if (isSystemUser && !isSystemManager) {
      // If document has no compliance template, don't show it to regular users
      if (!doc.templateId) {
        return false;
      }
      
      // Get the compliance template to check its owner_role
      const template = templateMapWithRoles[doc.templateId];
      if (!template) {
        // Template not found - don't show to regular users
        return false;
      }
      
      const templateOwnerRole = template.owner_role;
      if (!templateOwnerRole) {
        // Template has no owner_role - don't show to regular users
        return false;
      }
      
      // Check if the template's owner_role matches any of the user's owner roles
      return userOwnerRoles.includes(templateOwnerRole);
    }
    
    // Admin and Manager system roles see all contract types
    return true;
  });

  // Filter vendors by search term
  const filteredVendors = vendors.filter(vendor =>
    vendor.name.toLowerCase().includes(vendorSearchTerm.toLowerCase()) ||
    vendor.category?.toLowerCase().includes(vendorSearchTerm.toLowerCase()) ||
    vendor.contact?.toLowerCase().includes(vendorSearchTerm.toLowerCase()) ||
    vendor.email?.toLowerCase().includes(vendorSearchTerm.toLowerCase()) ||
    vendor.phone?.toLowerCase().includes(vendorSearchTerm.toLowerCase())
  );

  // Sort vendor contracts: Active first, then past contracts, both by upload date (newest first)
  const sortedVendorContracts = useMemo(() => {
    if (!vendorContracts.length) return [];

    const getDateValue = (date: any): Date | null => {
      if (!date) return null;
      if (date instanceof Date) return date;
      if (date?.toDate && typeof date.toDate === 'function') return date.toDate();
      if (typeof date === 'string') return new Date(date);
      if (typeof date === 'number') return new Date(date);
      return null;
    };

    const getUploadDate = (contract: AgreementCompletion): Date => {
      // Try signedAt first, then createdAt, then clientSignedAt
      const signedAt = getDateValue(contract.signedAt);
      if (signedAt) return signedAt;
      
      const createdAt = getDateValue(contract.createdAt);
      if (createdAt) return createdAt;
      
      if (contract.clientSignedAt) {
        return new Date(contract.clientSignedAt);
      }
      
      // Fallback to epoch if no date found
      return new Date(0);
    };

    const determineStatus = (contract: AgreementCompletion): string => {
      if (contract.status) return contract.status;
      
      const endDate = getDateValue(contract.endDate) || getDateValue(contract.expiryDate);
      const startDate = getDateValue(contract.startDate) || getDateValue(contract.contractDate);
      
      if (endDate && endDate < new Date()) {
        return 'Expired';
      } else if (startDate && endDate && startDate <= new Date() && endDate >= new Date()) {
        return 'Active';
      } else if (startDate && startDate > new Date()) {
        return 'Pending Renewal';
      }
      return 'Active';
    };

    return [...vendorContracts].sort((a, b) => {
      const statusA = determineStatus(a);
      const statusB = determineStatus(b);
      
      // Active contracts come first
      const isActiveA = statusA === 'Active';
      const isActiveB = statusB === 'Active';
      
      if (isActiveA && !isActiveB) return -1;
      if (!isActiveA && isActiveB) return 1;
      
      // Within same status group, sort by upload date (newest first)
      const uploadDateA = getUploadDate(a);
      const uploadDateB = getUploadDate(b);
      
      return uploadDateB.getTime() - uploadDateA.getTime();
    });
  }, [vendorContracts]);

  // Filter employees by search term
  const filteredEmployees = employees.filter(employee =>
    employee.name.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
    employee.category?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
    employee.contact?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
    employee.email?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
    employee.phone?.toLowerCase().includes(employeeSearchTerm.toLowerCase())
  );

  // Check if user has access to contracts management (comply tier only)
  if (!hasFeature('contracts_management')) {
    return (
      <div className="p-8">
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <PenTool className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Contracts Management</h2>
          <p className="text-gray-600 mb-6">
            Contracts management is available in the Comply tier. Please upgrade your subscription to access this feature.
          </p>
          <button
            onClick={() => navigate('/subscription')}
            className="btn-primary px-6 py-2"
          >
            View Subscription Plans
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-[95vw] mx-auto">
      <div className="bg-white rounded-lg shadow-md p-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
                <PenTool className="w-6 h-6 text-purple-600" />
                Contract Templates
              </h2>
              <ContextualHelp category="Contracts" tooltip="Help with Contracts" />
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {activeTab === 'agreements' ? 'Manage contract documents' : activeTab === 'vendors' ? 'Manage vendors' : 'Manage employees'}
            </p>
          </div>
          {activeTab === 'agreements' && isSystemManager && (
            <button
              onClick={handleAddClick}
              className="btn-primary px-4 py-2 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Contract Template
            </button>
          )}
          {activeTab === 'vendors' && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleBulkUploadClick}
                className="btn-primary px-4 py-2 flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
                title="Bulk upload vendor contracts with AI extraction"
              >
                <Sparkles className="w-4 h-4" />
                Bulk Upload Contracts
              </button>
              <button
                onClick={handleAddVendor}
                className="btn-primary px-4 py-2 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Vendor
              </button>
            </div>
          )}
          {activeTab === 'employees' && (
            <button
              onClick={handleAddEmployee}
              className="btn-primary px-4 py-2 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Employee
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('agreements')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'agreements'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Agreements
            </button>
            <button
              onClick={() => setActiveTab('vendors')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'vendors'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Vendors
            </button>
            <button
              onClick={() => setActiveTab('employees')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'employees'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Employees
            </button>
          </div>
        </div>

        {/* Agreements Tab Content */}
        {activeTab === 'agreements' && (
          <>
            {/* Info Banner */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>How it works:</strong> Add contract types and link them to your compliance items. Attach reference documents when needed, then keep uploading signed agreements for each type and review them as part of your compliance checks.
              </p>
            </div>

            {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search agreements... (AI-powered suggestions available)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md"
            />
            {isLoadingAI && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-orange-500 animate-spin" />
                <span className="text-xs text-orange-600 font-medium animate-pulse">AI searching...</span>
              </div>
            )}
          </div>
        </div>

        {/* Documents Table */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading documents...</p>
          </div>
        ) : filteredDocuments.length === 0 && !isLoading ? (
          <div>
            {isLoadingAI ? (
              <div className="text-center py-8 border border-gray-200 rounded">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Sparkles className="w-6 h-6 text-orange-500 animate-spin" />
                  <p className="text-lg font-medium text-gray-700">AI is finding suggestions...</p>
                </div>
                <p className="text-sm text-gray-500">Analyzing available templates for you</p>
              </div>
            ) : newAgreementSuggestions && newAgreementSuggestions.length === 0 && searchTerm.length >= 3 ? (
              <div className="border border-orange-200 rounded-lg p-6 bg-orange-50 text-left">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-orange-600" />
                  <h3 className="text-lg font-semibold text-orange-900">AI Search Complete</h3>
                </div>
              
              <p className="text-sm text-gray-700 mb-2">
                {templates.length === 0 
                  ? '⚠️ No compliance templates available. Please create templates first before creating agreements.'
                  : 'No AI-generated agreement suggestions found. Try a different search term or create an agreement manually.'}
              </p>
              
              {templates.length === 0 && (
                <button
                  onClick={() => window.location.href = '/compliance-management'}
                  className="mt-3 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm"
                >
                  Go to Templates →
                </button>
              )}
              </div>
            ) : newAgreementSuggestions && newAgreementSuggestions.length > 0 ? (
              <div className="border border-blue-200 rounded-lg p-6 bg-blue-50 text-left">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-blue-900">New Agreement Suggestions</h3>
                </div>
                
                <p className="text-sm text-gray-700 mb-4">
                  Based on your search, here are agreements you might want to add to your system:
                </p>

                {/* Show upgrade message for Starter plan */}
                {!hasFeature('ai_assistance') && (
                  <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-sm text-orange-800 font-medium mb-2">
                      Upgrade to Standard or Pro plan to add AI-generated agreements
                    </p>
                    <button
                      onClick={() => navigate('/subscription')}
                      className="px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium inline-flex items-center gap-1"
                    >
                      <Sparkles className="w-3 h-3" />
                      Upgrade Now
                    </button>
                  </div>
                )}
                
                <div className="space-y-4">
                  {newAgreementSuggestions.map((suggestion, index) => (
                    <div 
                      key={index}
                      className="bg-white rounded-lg p-5 border border-blue-200 hover:border-blue-300 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 text-lg mb-1">{suggestion.name}</h5>
                          {suggestion.description && (
                            <p className="text-sm text-gray-700 mb-2">{suggestion.description}</p>
                          )}
                          {suggestion.templateName && (
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                {suggestion.templateName}
                              </span>
                            </div>
                          )}
                          <p className="text-xs text-gray-600 italic mb-3">"{suggestion.why_relevant}"</p>
                          
                          {suggestion.suggestedSigners && suggestion.suggestedSigners.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="text-xs text-gray-700">
                                <span className="font-medium">Suggested Signers ({suggestion.suggestedSigners.length}):</span>
                                <ul className="mt-1 space-y-1 ml-4 list-disc">
                                  {suggestion.suggestedSigners.slice(0, 5).map((signer, idx) => (
                                    <li key={idx} className="text-gray-600">
                                      {signer.name} {signer.role && <span className="text-gray-400">({signer.role})</span>}
                                    </li>
                                  ))}
                                  {suggestion.suggestedSigners.length > 5 && (
                                    <li className="text-gray-500 italic">... and {suggestion.suggestedSigners.length - 5} more signers</li>
                                  )}
                                </ul>
                              </div>
                            </div>
                          )}
                          
                          {suggestion.documentContent && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="text-xs text-gray-700">
                                <span className="font-medium mb-2 block">Document Content:</span>
                                <div className="bg-gray-50 p-3 rounded border border-gray-200 max-h-40 overflow-y-auto">
                                  <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono">
                                    {suggestion.documentContent.substring(0, 300)}
                                    {suggestion.documentContent.length > 300 && '...'}
                                  </pre>
                                </div>
                                {suggestion.documentContent.length > 300 && (
                                  <p className="text-xs text-gray-500 mt-1 italic">Preview truncated - full content will be available after creation</p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <button
                        onClick={() => {
                          if (hasFeature('ai_assistance')) {
                            handleAddFromSuggestion(suggestion);
                          } else {
                            navigate('/subscription');
                          }
                        }}
                        disabled={!hasFeature('ai_assistance')}
                        className={`w-full mt-3 py-2 text-sm rounded-md transition-colors flex items-center justify-center gap-2 font-medium ${
                          hasFeature('ai_assistance')
                            ? 'bg-primary-600 text-white hover:bg-primary-700'
                            : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                        }`}
                      >
                        {hasFeature('ai_assistance') ? 'Add This Agreement' : 'Upgrade to Add'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
          <div className="text-center py-12 text-gray-500">
            <PenTool className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">No agreements</p>
            <p className="text-sm mt-1">Create your first agreement to get started</p>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Contract Template</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Compliance</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 border border-gray-200">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocuments.map((doc) => {
                  const complianceName = doc.templateId ? templateMap[doc.templateId] || 'Unknown' : 'Not assigned';

                  return (
                    <tr key={doc.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 border border-gray-200">
                        <div className="font-medium text-sm text-gray-900">{doc.name}</div>
                      </td>
                      <td className="px-4 py-3 border border-gray-200">
                        <div className="text-sm text-gray-700">
                          {doc.description || <span className="text-gray-400 italic">No description</span>}
                          {doc.fileUrl && (
                            <div className="mt-1.5">
                              <a
                                href={doc.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1.5 text-purple-600 hover:text-purple-800 hover:underline"
                                title="View Reference Document"
                              >
                                <FileText className="w-3.5 h-3.5" />
                                <span className="text-xs font-medium">Reference Document</span>
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 border border-gray-200">
                        <div className="text-sm text-gray-700">{complianceName}</div>
                      </td>
                      <td className="px-4 py-3 border border-gray-200">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleRunAgreement(doc)}
                            className="text-purple-600 hover:text-purple-800 px-2 py-1 rounded hover:bg-purple-50"
                            title="Add Signed Contract"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleViewHistory(doc)}
                            className="text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50"
                            title="View History"
                          >
                            <Files className="w-4 h-4" />
                          </button>
                          {isSystemManager && (
                            <>
                              <button
                                onClick={() => handleEdit(doc)}
                                className="text-green-600 hover:text-green-800 px-2 py-1 rounded hover:bg-green-50"
                                title="Edit Contract"
                              >
                                <PenTool className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(doc.id!)}
                                className="text-red-600 hover:text-red-800 px-2 py-1 rounded hover:bg-red-50"
                                title="Delete Agreement"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
                    </table>
                  </div>
                )}
          </>
        )}

        {/* Vendors Tab Content */}
        {activeTab === 'vendors' && (
          <>
            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search vendors..."
                  value={vendorSearchTerm}
                  onChange={(e) => setVendorSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            {/* Vendors Table */}
            {isLoadingVendors ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading vendors...</p>
              </div>
            ) : filteredVendors.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">No vendors</p>
                <p className="text-sm mt-1">Add your first vendor to get started</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto border-collapse">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Vendor Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Contact</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Locations</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 border border-gray-200">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVendors.map((vendor) => (
                      <tr 
                        key={vendor.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleVendorClick(vendor)}
                      >
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="font-medium text-sm text-gray-900">{vendor.name}</div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm text-gray-700">
                            {vendor.category || <span className="text-gray-400 italic">No category</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm text-gray-700">
                            {vendor.contact || <span className="text-gray-400 italic">No contact</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm text-gray-700">
                            {vendor.email ? (
                              <a href={`mailto:${vendor.email}`} className="text-purple-600 hover:text-purple-800 hover:underline">
                                {vendor.email}
                              </a>
                            ) : (
                              <span className="text-gray-400 italic">No email</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm text-gray-700">
                            {vendor.phone ? (
                              <a href={`tel:${vendor.phone}`} className="text-purple-600 hover:text-purple-800 hover:underline">
                                {vendor.phone}
                              </a>
                            ) : (
                              <span className="text-gray-400 italic">No phone</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm text-gray-700">
                            {vendor.locationIds && vendor.locationIds.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {vendor.locationIds.map(locationId => {
                                  const location = availableLocations.find(l => l.id === locationId);
                                  return location ? (
                                    <span
                                      key={locationId}
                                      className="inline-block px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded"
                                    >
                                      {location.name}
                                    </span>
                                  ) : (
                                    <span key={locationId} className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                      {locationId.substring(0, 8)}...
                                    </span>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">No locations</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleOpenVendorConversation(vendor)}
                              className={`px-2 py-1 rounded hover:bg-gray-50 ${
                                vendor.id && vendorConversations.has(vendor.id)
                                  ? 'text-blue-600 hover:text-blue-800'
                                  : 'text-gray-600 hover:text-gray-800'
                              }`}
                              title={vendor.id && vendorConversations.has(vendor.id) ? 'Open Vendor Conversation (conversation exists)' : 'Open Vendor Conversation'}
                            >
                              <MessageSquare className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditVendor(vendor)}
                              className="text-purple-600 hover:text-purple-800 px-2 py-1 rounded hover:bg-purple-50"
                              title="Edit Vendor"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => vendor.id && handleDeleteVendorClick(vendor.id)}
                              className="text-red-600 hover:text-red-800 px-2 py-1 rounded hover:bg-red-50"
                              title="Delete Vendor"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Employees Tab Content */}
        {activeTab === 'employees' && (
          <>
            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={employeeSearchTerm}
                  onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            {/* Employees Table */}
            {isLoadingEmployees ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading employees...</p>
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">No employees</p>
                <p className="text-sm mt-1">Add your first employee to get started</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto border-collapse">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Employee Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Contact</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Locations</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 border border-gray-200">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map((employee) => (
                      <tr key={employee.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="font-medium text-sm text-gray-900">{employee.name}</div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm text-gray-700">
                            {employee.category || <span className="text-gray-400 italic">No category</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm text-gray-700">
                            {employee.contact || <span className="text-gray-400 italic">No contact</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm text-gray-700">
                            {employee.email ? (
                              <a href={`mailto:${employee.email}`} className="text-purple-600 hover:text-purple-800 hover:underline">
                                {employee.email}
                              </a>
                            ) : (
                              <span className="text-gray-400 italic">No email</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm text-gray-700">
                            {employee.phone ? (
                              <a href={`tel:${employee.phone}`} className="text-purple-600 hover:text-purple-800 hover:underline">
                                {employee.phone}
                              </a>
                            ) : (
                              <span className="text-gray-400 italic">No phone</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm text-gray-700">
                            {employee.locationIds && employee.locationIds.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {employee.locationIds.map(locationId => {
                                  const location = availableLocations.find(l => l.id === locationId);
                                  return location ? (
                                    <span
                                      key={locationId}
                                      className="inline-block px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded"
                                    >
                                      {location.name}
                                    </span>
                                  ) : (
                                    <span key={locationId} className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                      {locationId.substring(0, 8)}...
                                    </span>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">No locations</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleOpenEmployeeConversation(employee)}
                              className="text-gray-600 hover:text-gray-800 px-2 py-1 rounded hover:bg-gray-50"
                              title="Open Employee Conversation"
                            >
                              <MessageSquare className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditEmployee(employee)}
                              className="text-purple-600 hover:text-purple-800 px-2 py-1 rounded hover:bg-purple-50"
                              title="Edit Employee"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => employee.id && handleDeleteEmployeeClick(employee.id)}
                              className="text-red-600 hover:text-red-800 px-2 py-1 rounded hover:bg-red-50"
                              title="Delete Employee"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Employee Form Modal */}
      {showEmployeeForm && (
        <EmployeeForm
          employee={selectedEmployee || undefined}
          onSubmit={handleSaveEmployee}
          onCancel={handleCancelEmployee}
        />
      )}

      {/* Delete Employee Confirmation Modal */}
      {showDeleteEmployeeConfirm && employeeToDelete && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={() => setShowDeleteEmployeeConfirm(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Delete Employee
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to delete <span className="font-semibold text-gray-900">"{employeeToDelete.name}"</span>?
                </p>
                <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                  <p className="text-xs text-red-800">
                    <strong>Warning:</strong> This action cannot be undone. Any contracts linked to this employee will remain but the employee reference will be removed.
                  </p>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowDeleteEmployeeConfirm(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteEmployeeConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                  >
                    Delete Employee
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vendor Form Modal */}
      {showVendorForm && (
        <VendorForm
          vendor={selectedVendor || undefined}
          onSubmit={handleSaveVendor}
          onCancel={handleCancelVendor}
        />
      )}

      {/* Bulk Upload Vendor Contracts Modal */}
      {showBulkUploadModal && (
        <BulkUploadVendorContracts
          onClose={() => setShowBulkUploadModal(false)}
          onComplete={async () => {
            setShowBulkUploadModal(false);
            // Ensure we're on the vendors tab to see the new vendors
            setActiveTab('vendors');
            // Refresh vendors and agreements to show newly created ones
            await loadVendors();
            await loadDocuments();
            showNotification('Vendors and contracts have been updated', 'success');
          }}
        />
      )}

      {/* Vendor Detail Modal */}
      {showVendorDetailModal && selectedVendorForDetail && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={() => setShowVendorDetailModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-5 h-5 text-purple-600" />
                    <h2 className="text-xl font-semibold text-gray-900">{selectedVendorForDetail.name}</h2>
                    {selectedVendorForDetail.category && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                        {selectedVendorForDetail.category}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                    {selectedVendorForDetail.contact && (
                      <span className="flex items-center gap-1">
                        <span className="font-medium">Contact:</span>
                        <span>{selectedVendorForDetail.contact}</span>
                      </span>
                    )}
                    {selectedVendorForDetail.email && (
                      <a 
                        href={`mailto:${selectedVendorForDetail.email}`} 
                        className="flex items-center gap-1 text-purple-600 hover:text-purple-800 hover:underline"
                      >
                        <span className="font-medium">Email:</span>
                        <span>{selectedVendorForDetail.email}</span>
                      </a>
                    )}
                    {selectedVendorForDetail.phone && (
                      <a 
                        href={`tel:${selectedVendorForDetail.phone}`} 
                        className="flex items-center gap-1 text-purple-600 hover:text-purple-800 hover:underline"
                      >
                        <span className="font-medium">Phone:</span>
                        <span>{selectedVendorForDetail.phone}</span>
                      </a>
                    )}
                  </div>
                  {selectedVendorForDetail.locationIds && selectedVendorForDetail.locationIds.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <span className="text-xs font-medium text-gray-600">Locations:</span>
                      {selectedVendorForDetail.locationIds.map(locationId => {
                        const location = availableLocations.find(l => l.id === locationId);
                        return location ? (
                          <span
                            key={locationId}
                            className="inline-block px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded"
                          >
                            {location.name}
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setShowVendorDetailModal(false)}
                  className="text-gray-500 hover:text-gray-700 p-1 flex-shrink-0"
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Tabs */}
              <div className="border-b border-gray-200 mb-4">
                <nav className="flex space-x-8" aria-label="Tabs">
                  <button
                    onClick={() => setVendorDetailTab('contracts')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      vendorDetailTab === 'contracts'
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Contracts ({vendorContracts.length})
                  </button>
                  <button
                    onClick={() => setVendorDetailTab('documents')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      vendorDetailTab === 'documents'
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Supplementary Documents ({selectedVendorForDetail?.supplementaryDocuments?.length || 0})
                  </button>
                </nav>
              </div>

              {/* Contracts Tab */}
              {vendorDetailTab === 'contracts' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Contracts ({vendorContracts.length})
                </h3>
                {isLoadingVendorContracts ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="text-gray-600 mt-2">Loading contracts...</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full table-auto border-collapse">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Location</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Contract Name</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Asset Types</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Key Dates</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">
                            <div className="flex items-center gap-1.5">
                              <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                              <span>AI Summary</span>
                            </div>
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 border border-gray-200">Links</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedVendorContracts.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-4 py-8 text-center text-gray-500 border border-gray-200">
                              <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                              <p className="text-sm">No contracts found for this vendor</p>
                            </td>
                          </tr>
                        ) : (
                          sortedVendorContracts.map((contract, index) => {
                            // Helper function to get date value
                            const getDateValue = (date: any): Date | null => {
                              if (!date) return null;
                              if (date instanceof Date) return date;
                              if (date?.toDate && typeof date.toDate === 'function') return date.toDate();
                              if (typeof date === 'string') return new Date(date);
                              if (typeof date === 'number') return new Date(date);
                              return null;
                            };

                            const contractDate = getDateValue(contract.contractDate) || getDateValue(contract.signedAt);
                            const expiryDate = getDateValue(contract.expiryDate);
                            const signedDate = getDateValue(contract.signedAt);
                            const startDate = getDateValue(contract.startDate);
                            const endDate = getDateValue(contract.endDate);

                            const locationName = contract.locationId 
                              ? availableLocations.find(l => l.id === contract.locationId)?.name || contract.locationId
                              : 'Unknown Location';

                            // Determine status from contract.status field or calculate from dates
                            let contractStatus = contract.status;
                            if (!contractStatus) {
                              // Fallback: calculate status from dates if status field is not set
                              if (endDate && endDate < new Date()) {
                                contractStatus = 'Expired';
                              } else if (startDate && endDate && startDate <= new Date() && endDate >= new Date()) {
                                contractStatus = 'Active';
                              } else if (startDate && startDate > new Date()) {
                                contractStatus = 'Pending Renewal';
                              } else {
                                contractStatus = 'Active'; // Default
                              }
                            }

                            // Get asset type names from fieldValues.assetTypeIds
                            const assetTypeIds = contract.fieldValues?.assetTypeIds as string[] || [];
                            const assetTypeNames = assetTypeIds
                              .map(id => assetTypes.find(at => at.id === id)?.name)
                              .filter(Boolean) as string[];

                            const isExpired = expiryDate && expiryDate < new Date();
                            const isExpiringSoon = expiryDate && expiryDate > new Date() && expiryDate <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

                            // Status badge styling
                            const getStatusBadge = (status: string) => {
                              switch (status) {
                                case 'Active':
                                  return <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">Active</span>;
                                case 'Expired':
                                  return <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-medium rounded">Expired</span>;
                                case 'Pending Renewal':
                                  return <span className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs font-medium rounded">Pending Renewal</span>;
                                case 'Terminated':
                                  return <span className="px-2 py-0.5 bg-gray-100 text-gray-800 text-xs font-medium rounded">Terminated</span>;
                                default:
                                  return <span className="px-2 py-0.5 bg-gray-100 text-gray-800 text-xs font-medium rounded">{status || 'Unknown'}</span>;
                              }
                            };

                            return (
                              <tr key={contract.id} className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                {/* Status */}
                                <td className="px-4 py-3 border border-gray-200">
                                  {getStatusBadge(contractStatus)}
                                </td>

                                {/* Location */}
                                <td className="px-4 py-3 border border-gray-200">
                                  <div className="flex items-center gap-2">
                                    <Building2 className="w-4 h-4 text-purple-600" />
                                    <span className="text-sm text-gray-900">{locationName}</span>
                                  </div>
                                </td>

                                {/* Contract Name */}
                                <td className="px-4 py-3 border border-gray-200">
                                  <div className="text-sm font-medium text-gray-900">{contract.agreementName}</div>
                                </td>

                                {/* Asset Types */}
                                <td className="px-4 py-3 border border-gray-200">
                                  {assetTypeNames.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {assetTypeNames.map((name, idx) => (
                                        <span
                                          key={idx}
                                          className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded"
                                        >
                                          {name}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 italic text-xs">No asset types</span>
                                  )}
                                </td>

                                {/* Key Dates */}
                                <td className="px-4 py-3 border border-gray-200">
                                  <div className="space-y-1 text-xs">
                                    {(startDate || contractDate) && (
                                      <div>
                                        <span className="font-medium text-gray-600">Start: </span>
                                        <span className="text-gray-900">{(startDate || contractDate)?.toLocaleDateString()}</span>
                                      </div>
                                    )}
                                    {endDate && (
                                      <div>
                                        <span className="font-medium text-gray-600">Expires: </span>
                                        <span className={`${isExpired ? 'text-red-600' : isExpiringSoon ? 'text-orange-600' : 'text-gray-900'}`}>
                                          {endDate.toLocaleDateString()}
                                        </span>
                                        {isExpired && <span className="ml-1 text-red-600 text-xs">(Expired)</span>}
                                        {isExpiringSoon && <span className="ml-1 text-orange-600 text-xs">(Expiring Soon)</span>}
                                      </div>
                                    )}
                                    {expiryDate && !endDate && (
                                      <div>
                                        <span className="font-medium text-gray-600">Expires: </span>
                                        <span className={`${isExpired ? 'text-red-600' : isExpiringSoon ? 'text-orange-600' : 'text-gray-900'}`}>
                                          {expiryDate.toLocaleDateString()}
                                        </span>
                                        {isExpired && <span className="ml-1 text-red-600 text-xs">(Expired)</span>}
                                        {isExpiringSoon && <span className="ml-1 text-orange-600 text-xs">(Expiring Soon)</span>}
                                      </div>
                                    )}
                                    {signedDate && (
                                      <div>
                                        <span className="font-medium text-gray-600">Signed: </span>
                                        <span className="text-gray-900">{signedDate.toLocaleDateString()}</span>
                                      </div>
                                    )}
                                    {!startDate && !contractDate && !endDate && !expiryDate && !signedDate && (
                                      <span className="text-gray-400 italic">No dates available</span>
                                    )}
                                  </div>
                                </td>

                                {/* AI Summary */}
                                <td className="px-4 py-3 border border-gray-200">
                                  {contract.contractSummary ? (
                                    <div className="text-xs text-gray-700 max-w-md whitespace-pre-wrap break-words">
                                      {contract.contractSummary}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 italic text-xs">No summary available</span>
                                  )}
                                </td>

                                {/* Links */}
                                <td className="px-4 py-3 border border-gray-200">
                                  <div className="flex items-center justify-center gap-2">
                                    {contract.signedPdfUrl ? (
                                      <a
                                        href={contract.signedPdfUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-purple-600 hover:text-purple-800 px-2 py-1 rounded hover:bg-purple-50 flex items-center gap-1"
                                        title="View PDF"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <FileText className="w-4 h-4" />
                                        <span className="text-xs">PDF</span>
                                        <ExternalLink className="w-3 h-3" />
                                      </a>
                                    ) : (
                                      <span className="text-gray-400 text-xs italic">No PDF</span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}

              {/* Supplementary Documents Tab */}
              {vendorDetailTab === 'documents' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Supplementary Documents ({selectedVendorForDetail?.supplementaryDocuments?.length || 0})
                    </h3>
                    {selectedVendorForDetail?.id && (
                      <label className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 cursor-pointer">
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Document
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                          multiple
                          onChange={(e) => handleUploadSupplementaryDocument(e)}
                        />
                      </label>
                    )}
                  </div>
                  {!selectedVendorForDetail?.supplementaryDocuments || selectedVendorForDetail.supplementaryDocuments.length === 0 ? (
                    <div className="text-center py-12 border border-gray-200 rounded-lg bg-gray-50">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm text-gray-500">No supplementary documents found</p>
                      <p className="text-xs text-gray-400 mt-1">Click "Upload Document" to add documents</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full table-auto border-collapse">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Document Name</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Type</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Uploaded</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">Size</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 border border-gray-200">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedVendorForDetail.supplementaryDocuments.map((doc, index) => {
                            const uploadedDate = doc.uploadedAt?.toDate ? doc.uploadedAt.toDate() : 
                                                 doc.uploadedAt instanceof Date ? doc.uploadedAt :
                                                 doc.uploadedAt ? new Date(doc.uploadedAt) : null;
                            
                            const formatFileSize = (bytes?: number) => {
                              if (!bytes) return 'Unknown';
                              if (bytes < 1024) return `${bytes} B`;
                              if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
                              return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
                            };

                            const getDocumentTypeLabel = (type: string) => {
                              const labels: Record<string, string> = {
                                communication: 'Communication',
                                amendment: 'Amendment',
                                certificate: 'Certificate',
                                license: 'License',
                                other: 'Other',
                              };
                              return labels[type] || type;
                            };

                            return (
                              <tr key={doc.id} className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                <td className="px-4 py-3 border border-gray-200">
                                  <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-purple-600" />
                                    <span className="text-sm text-gray-900">{doc.fileName}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 border border-gray-200">
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                    {getDocumentTypeLabel(doc.documentType)}
                                  </span>
                                </td>
                                <td className="px-4 py-3 border border-gray-200">
                                  <span className="text-xs text-gray-600">
                                    {uploadedDate ? uploadedDate.toLocaleDateString() : 'Unknown'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 border border-gray-200">
                                  <span className="text-xs text-gray-600">{formatFileSize(doc.fileSize)}</span>
                                </td>
                                <td className="px-4 py-3 border border-gray-200">
                                  <div className="flex items-center justify-center gap-2">
                                    <a
                                      href={doc.fileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-purple-600 hover:text-purple-800 px-2 py-1 rounded hover:bg-purple-50 flex items-center gap-1"
                                      title="View Document"
                                    >
                                      <FileText className="w-4 h-4" />
                                      <span className="text-xs">View</span>
                                      <ExternalLink className="w-3 h-3" />
                                    </a>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Summary Modal */}
      {showAISummaryModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={() => setShowAISummaryModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-900">AI Summary</h2>
              </div>
              <button
                onClick={() => setShowAISummaryModal(false)}
                className="text-gray-500 hover:text-gray-700 p-1"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="prose prose-sm max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap">{selectedContractSummary}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Vendor Confirmation Modal */}
      {showDeleteVendorConfirm && vendorToDelete && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={() => setShowDeleteVendorConfirm(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Delete Vendor
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to delete <span className="font-semibold text-gray-900">"{vendorToDelete.name}"</span>?
                </p>
                <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                  <p className="text-xs text-red-800">
                    <strong>Warning:</strong> This action cannot be undone. Any contracts linked to this vendor will remain but the vendor reference will be removed.
                  </p>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowDeleteVendorConfirm(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteVendorConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                  >
                    Delete Vendor
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Form Modal */}
      {showDocumentForm && (
        <AgreementDocumentForm
          document={isEditing ? (selectedDocument || undefined) : (agreementToAdd as AgreementDocument || undefined)}
          aiGeneratedContent={!isEditing ? agreementDocumentContent || undefined : undefined}
          onClose={() => {
            setShowDocumentForm(false);
            setIsEditing(false);
            setSelectedDocument(null);
            setAgreementToAdd(null);
            setAgreementDocumentContent(null);
          }}
          onSave={async () => {
            await loadDocuments();
            setShowDocumentForm(false);
            setIsEditing(false);
            setSelectedDocument(null);
            setAgreementToAdd(null);
            setAgreementDocumentContent(null);
          }}
          availableLocations={availableLocations}
          organizationId={currentOrganization?.id}
          userId={user?.id}
          userName={user?.name || ''}
          userEmail={user?.email || ''}
          setShowOrgModal={setShowOrgModal}
        />
      )}

      {/* View Document Modal */}
      {showViewModal && selectedDocument && (
        <AgreementViewModal
          document={selectedDocument}
          onClose={() => {
            setShowViewModal(false);
            setSelectedDocument(null);
          }}
        />
      )}

      {/* Run Agreement Modal */}
      {isRunning && selectedDocument && (
        <RunAgreement
          agreement={selectedDocument}
          onClose={handleCloseRunAgreement}
          onComplete={() => {
            handleCloseRunAgreement();
          }}
        />
      )}

      {/* History Modal */}
      {showHistory && historyDocument && (
        <RunAgreement
          agreement={historyDocument}
          onClose={handleCloseRunAgreement}
          onComplete={handleCloseRunAgreement}
          viewMode="history"
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && documentToDelete && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={handleDeleteCancel}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Delete Contract
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to delete the contract template <span className="font-semibold text-gray-900">"{documentToDelete.name}"</span>?
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> Past uploaded contracts in the Evidence Vault will remain intact. You will only lose the ability to upload new contracts using this template.
                  </p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                  <p className="text-xs text-red-800">
                    <strong>Warning:</strong> This action cannot be undone. You will no longer be able to use this contract template to upload new contracts.
                  </p>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={handleDeleteCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Delete Contract
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Organization Required Modal */}
      {showOrgModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={() => setShowOrgModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Organization Required</h3>
                <p className="text-sm text-gray-600">Action Required</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-700 mb-6">
              You must have an organization to add agreements. Please create an organization first.
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowOrgModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowOrgModal(false);
                  navigate('/organizations');
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
              >
                Go to Organizations
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Getting Started button on empty state */}
      {documents.length === 0 && !isLoading && !showOrgModal && (
        <a
          href="/help/getting-started"
          className="fixed bottom-24 right-4 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg px-4 py-2 z-50"
          title="Getting Started"
        >
          Getting Started
        </a>
      )}
    </div>
  );
};

// View Document Modal Component
interface AgreementViewModalProps {
  document: AgreementDocument;
  onClose: () => void;
}

const AgreementViewModal: React.FC<AgreementViewModalProps> = ({ document, onClose }) => {
  const { currentOrganization } = useOrganization();
  const [complianceName, setComplianceName] = useState<string>('Loading...');

  useEffect(() => {
    if (document.templateId) {
      const loadCompliance = async () => {
        const result = await complianceService.getTemplate(document.templateId!);
        if (result.success && result.data) {
          setComplianceName(result.data.name);
        } else {
          setComplianceName('Unknown');
        }
      };
      loadCompliance();
    } else {
      setComplianceName('Not assigned');
    }
  }, [document.templateId]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[130] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-white border-b border-gray-200 px-4 py-2 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900 truncate flex-1 mr-2">{document.name}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-1 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
            {document.description && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-0.5">Description</label>
                <p className="text-xs text-gray-900 truncate">{document.description}</p>
              </div>
            )}
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">Compliance</label>
              <p className="text-xs text-gray-900 truncate">{complianceName}</p>
            </div>
            
            {document.vendorName && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-0.5">Vendor</label>
                <p className="text-xs text-gray-900 truncate">
                  {document.vendorName}
                  {document.vendorId && (
                    <span className="text-purple-600 hover:text-purple-800 hover:underline ml-1 cursor-pointer" title="View vendor details">
                      (view)
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            {document.fileUrl ? (
              <div>
                <div className="border border-gray-300 rounded-lg bg-gray-50">
                  <div className="flex items-center justify-between p-2 border-b border-gray-300">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-600" />
                      <a
                        href={document.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline text-xs"
                      >
                        View Document
                      </a>
                    </div>
                  </div>
                  <iframe
                    src={document.fileUrl}
                    className="w-full h-[calc(90vh-200px)] border-0"
                    title="Document Preview"
                  />
                </div>
              </div>
            ) : (
              <div className="border border-gray-300 rounded-lg bg-gray-50 p-8 text-center">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600">No reference document attached</p>
              </div>
            )}

            {document.signers && document.signers.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Signers</label>
                <div className="space-y-2">
                  {document.signers.map((signer, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{signer.name}</div>
                        <div className="text-xs text-gray-600">{signer.email}</div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${
                        signer.status === 'signed' ? 'bg-green-100 text-green-800' :
                        signer.status === 'declined' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {signer.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 p-2 border-t">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Document Form Component
interface AgreementDocumentFormProps {
  document?: AgreementDocument;
  onClose: () => void;
  onSave: () => void;
  availableLocations: Location[];
  organizationId?: string;
  userId?: string;
  userName: string;
  userEmail: string;
  aiGeneratedContent?: string; // Optional AI-generated document content
  setShowOrgModal: (show: boolean) => void;
}

const AgreementDocumentForm: React.FC<AgreementDocumentFormProps> = ({
  document,
  onClose,
  onSave,
  availableLocations,
  organizationId,
  userId,
  userName,
  userEmail,
  aiGeneratedContent,
  setShowOrgModal
}) => {
  const { showNotification } = useNotification();
  const { currentOrganization } = useOrganization();
  const { subscription } = useSubscription();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templateId, setTemplateId] = useState<string>('');
  const [contractType, setContractType] = useState<'vendor_contract' | 'employee_contract' | ''>('');
  const [templates, setTemplates] = useState<ComplianceTemplate[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadTemplates();
    if (document) {
      setName(document.name || '');
      setDescription(document.description || '');
      setTemplateId(document.templateId || '');
      setContractType(document.contractType || '');
    }
  }, [currentOrganization?.id, document]);

  const loadTemplates = async () => {
    const result = await complianceService.getAllTemplates(currentOrganization?.id);
    if (result.success && result.data) {
      setTemplates(result.data);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type - only PDF allowed
      const validTypes = ['application/pdf'];
      const validExtensions = ['.pdf'];
      
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      const isValidType = validTypes.includes(file.type) || validExtensions.includes(fileExtension);
      
      if (!isValidType) {
        showNotification('Please upload a PDF document (.pdf only)', 'error');
        e.target.value = ''; // Reset input
        return;
      }

      // Check file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        showNotification('File size must be less than 10MB', 'error');
        e.target.value = ''; // Reset input
        return;
      }

      setSelectedFile(file);
    }
  };

  const getFileType = (file: File): string => {
    return file.type || 'application/pdf';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if subscription is cancelled before creating new contract template
    if (!document?.id && subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to create contract templates.', 'error');
      navigate('/subscription');
      return;
    }
    
    if (!currentOrganization?.id) {
      setShowOrgModal(true);
      onSave(); // Close the form
      return;
    }
    
    if (!name.trim()) {
      showNotification('Please enter a document name', 'error');
      return;
    }

    if (!templateId) {
      showNotification('Please select a compliance template', 'error');
      return;
    }

    if (!contractType) {
      showNotification('Please select a contract type', 'error');
      return;
    }

    // Reference document is optional - only validate if user is trying to upload a new file
    if (selectedFile) {
      // File validation will be handled in the upload section
    }

    if (!organizationId) {
      showNotification('Organization ID is required', 'error');
      return;
    }

    setIsSubmitting(true);
    let finalFileUrl: string | undefined;
    let finalFileType: string | undefined;
    
    // Only upload if a new file is selected
    if (selectedFile) {
      setIsUploading(true);
      try {
        finalFileUrl = await uploadAgreementDocument(selectedFile, {
          docType: 'agreement_template',
          organizationId: organizationId || currentOrganization?.id,
          templateId,
          uploadedBy: userId
            ? { id: userId, email: userEmail, name: userName }
            : undefined,
          title: name.trim() || selectedFile.name,
          source: 'web',
        });
        finalFileType = getFileType(selectedFile);
      } catch (error: any) {
        showNotification(`Upload failed: ${error.message}`, 'error');
        setIsUploading(false);
        setIsSubmitting(false);
        return;
      }
      setIsUploading(false);
    } else if (document?.fileUrl) {
      // Use existing file
      finalFileUrl = document.fileUrl;
      finalFileType = document.fileType;
    }
    
    try {
      if (document?.id) {
        // Update existing document
        const updateData: Partial<AgreementDocument> = {
          name: name.trim(),
          description: description.trim() || undefined,
          templateId: templateId,
          contractType: contractType || undefined,
        };
        if (finalFileUrl) {
          updateData.fileUrl = finalFileUrl;
          if (finalFileType) {
            updateData.fileType = finalFileType;
          }
        }
        const result = await agreementService.updateDocument(document.id, updateData);

        if (result.success) {
          showNotification('Document updated successfully', 'success');
          onSave();
        } else {
          showNotification(`Error: ${result.error}`, 'error');
        }
      } else {
        // Create new document
        const result = await agreementService.createDocument({
          name: name.trim(),
          description: description.trim() || undefined,
          fileUrl: finalFileUrl,
          fileType: finalFileType,
          status: 'sent', // Documents are always ready to be sent for signatures
          signers: [],
          organizationId,
          templateId: templateId,
          contractType: contractType || undefined,
          createdBy: userId ? `${userName} (${userEmail})` : undefined,
        });

        if (result.success) {
          showNotification('Document created successfully', 'success');
          onSave();
        } else {
          showNotification(`Error: ${result.error}`, 'error');
        }
      }
    } catch (error) {
      console.error('Error saving document:', error);
      showNotification('Failed to save document', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-y-0 right-0 w-[600px] bg-white shadow-2xl border-l border-gray-200 z-[120] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <h2 className="text-lg font-semibold text-gray-800">
          {document ? 'Edit Contract Template' : 'Add Contract Template'}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 p-1"
          title="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Form Content - Scrollable */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <form id="agreement-form" onSubmit={handleSubmit} className="p-4 space-y-3">
          {/* Row 1: Name and Compliance */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Document name"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Compliance <span className="text-red-600">*</span>
              </label>
              <select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                required
              >
                <option value="">Select...</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>{template.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Contract Type */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Contract Type <span className="text-red-600">*</span>
            </label>
            <select
              value={contractType}
              onChange={(e) => setContractType(e.target.value as 'vendor_contract' | 'employee_contract' | '')}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              required
            >
              <option value="">Select Contract Type...</option>
              <option value="vendor_contract">Vendor Contract</option>
              <option value="employee_contract">Employee Contract</option>
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description..."
              rows={2}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            />
          </div>

          {/* AI Generated Document Content */}
          {aiGeneratedContent && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                AI Generated Document Content
              </label>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-xs text-blue-900 font-medium">
                    This AI-generated contract template is ready for you to download and customize
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      // Create a blob and download
                      const blob = new Blob([aiGeneratedContent], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const link = window.document.createElement('a');
                      link.href = url;
                      link.download = `${name || 'agreement'}_template.txt`;
                      window.document.body.appendChild(link);
                      link.click();
                      window.document.body.removeChild(link);
                      URL.revokeObjectURL(url);
                      showNotification('Document template downloaded', 'success');
                    }}
                    className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                  >
                    <Download className="w-3 h-3" />
                    Download as Text
                  </button>
                </div>
                <div className="max-h-40 overflow-y-auto bg-white border border-blue-200 rounded p-3">
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">
                    {aiGeneratedContent}
                  </pre>
                </div>
                <p className="text-xs text-blue-700 mt-2">
                  💡 Tip: Download this content, customize it in Word/Pages, save as PDF, then upload above
                </p>
              </div>
            </div>
          )}

          {/* Document Upload */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Reference Document (Optional)
              {document && <span className="text-xs text-gray-500 ml-1">(Leave empty to keep current)</span>}
            </label>
            <label 
              htmlFor="file-upload"
              className="flex flex-col items-center justify-center w-full h-28 border-2 border-gray-300 border-dashed rounded-md cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex flex-col items-center justify-center pt-4 pb-3 pointer-events-none">
                <Upload className="w-6 h-6 mb-1.5 text-gray-500" />
                <p className="mb-1 text-xs text-gray-500">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                      <p className="text-xs text-gray-400">PDF document (MAX. 10MB)</p>
                    </div>
                    <input
                      id="file-upload"
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={handleFileChange}
                      className="hidden"
                    />
            </label>
            {selectedFile && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-green-600" />
                    <span className="text-xs text-gray-700">{selectedFile.name}</span>
                    <span className="text-xs text-gray-500">
                      ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedFile(null);
                      const fileInput = window.document.querySelector('input[type="file"]') as HTMLInputElement;
                      if (fileInput) fileInput.value = '';
                    }}
                    className="text-red-600 hover:text-red-800"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
                  <p className="text-xs text-gray-500 mt-1">
                    Supported format: PDF (.pdf) only - Max size: 10MB
                  </p>
          </div>
        </form>
      </div>

      {/* Footer with buttons */}
      <div className="border-t border-gray-200 bg-gray-50 p-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="btn-secondary px-4 py-2 text-sm"
          disabled={isSubmitting || isUploading}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="agreement-form"
          className="btn-primary px-4 py-2 text-sm"
          disabled={isSubmitting || isUploading}
        >
              {isUploading ? 'Uploading...' : isSubmitting ? (document ? 'Updating...' : 'Creating...') : (document ? 'Update Contract' : 'Create Contract')}
        </button>
      </div>
    </div>
  );
};

export default AgreementManagement;

